import { useState } from 'react';
import { getApiV1MissionsMissionIdReportFile } from '@/shared/api/generated/missions/missions';
import { useLocale } from '@/shared/lib/i18n/locale-context';

/**
 * Opens the filed report as a PDF (§19's report generation).
 *
 * ⚠️ The link is fetched at the moment of the click, never with the mission — the same rule
 * `DocumentLink` follows, and for the same two reasons that outlive it: the URL expires in minutes,
 * so one fetched with the page is stale before anybody presses it, and a presigned URL rendered into
 * a page is a bearer credential sitting in the DOM.
 */
export function ReportFileLink({ missionId }: { missionId: string }) {
  const { t } = useLocale();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const open = async () => {
    setBusy(true);
    setError(null);

    try {
      const url = await getApiV1MissionsMissionIdReportFile(missionId);

      if (!url) {
        setError(t.missions.detailReportFileUnavailable);
        return;
      }

      // `noopener` is not optional. The document is served from the storage origin, and without it
      // the opened tab keeps a handle on `window.opener` — a reference to an authenticated admin
      // session.
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      setError(t.missions.detailReportFileUnavailable);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-1">
      <button
        type="button"
        onClick={() => void open()}
        disabled={busy}
        className="text-start text-success underline underline-offset-4 disabled:opacity-60"
      >
        {busy ? t.missions.detailReportFileOpening : t.missions.detailReportFile}
      </button>

      {error ? <span className="text-xs text-danger">{error}</span> : null}
    </div>
  );
}
